import type { Tab } from "app/useTabs";
import classnames from "classnames";
import { routes } from "pages/routes";
import { Link } from "react-router-dom";
import "./NavPanel.css";

type Props = {
  tabs: Tab[];
  linkClassName?: string;
};

const NavPanel = ({ tabs, linkClassName }: Props) => {
  const currentPathname = window.location.pathname;

  const tabsItems = tabs.map(tab => {
    const itemClassName = classnames(linkClassName, { "as-icon": tab.asIcon });

    if (tab.path) {
      const isSelected = resolveActivePath(tab.path, currentPathname) === tab.path;
      const stateClassName = isSelected ? "selected" : "unselected";

      return (
        <Link
          key={tab.name}
          to={tab.path}
          className={stateClassName}
          aria-current={isSelected ? "page" : undefined}
        >
          <NavPanelInnerItem tab={tab} />
        </Link>
      );
    }

    if (tab.absolutePath) {
      return (
        <a
          href={tab.absolutePath}
          key={tab.name}
          className={itemClassName}
          target={tab.external ? "_blank" : "_self"}
          rel="noreferrer"
        >
          <NavPanelInnerItem tab={tab} />
        </a>
      );
    }

    if (tab.onClick) {
      return (
        <button
          type="button"
          key={tab.name}
          className={classnames("bare-button", itemClassName)}
          onClick={tab.onClick}
        >
          <NavPanelInnerItem tab={tab} />
        </button>
      );
    }

    return null;
  });

  return <nav className="tabs">{tabsItems}</nav>;
};

const resolveActivePath = (path: string, currentPathname: string): string | undefined => {
  if (currentPathname.includes(path)) {
    if (path !== "/admin") {
      return path;
    }

    if (
      !currentPathname.startsWith("/admin/content") &&
      !currentPathname.startsWith("/admin/settings")
    ) {
      return path;
    }
  }

  // Before the catalog's check: a student's `/student/:id/lessons` contains "/lesson".
  if (routes.studentDetails.match(currentPathname)) {
    return routes.students.path();
  }

  // A course and a lesson are read on routes of their own, under the catalog's tab.
  if (currentPathname.includes("/course") || currentPathname.includes("/lesson")) {
    return "/library";
  }

  return undefined;
};

type NavPanelInnerItemProps = {
  tab: Tab;
};

const NavPanelInnerItem = ({ tab }: NavPanelInnerItemProps) => {
  if (tab.asIcon && tab.icon) {
    // If nav tab is supposed to be rendered as icon, then render icon.
    return <>{tab.icon}</>;
  }

  // Otherwise render just text.
  return <>{tab.title}</>;
};

export default NavPanel;
